import {KEYUTIL, KJUR, RSAKey} from 'jsrsasign';
import type {FlattenedJwsInput, JsonWebKeySet, Jwk, JwsHeaderParameters, KeyLike} from '../types.ts';
import {JoseNotSupported, JwksInvalid, JwksNoMatchingKey} from '../errors.ts';

function getKtyFromAlg(alg: unknown) {
    switch (typeof alg === 'string' && alg.slice(0, 2)) {
        case 'RS':
        case 'PS':
            return 'RSA'
        case 'ES':
            return 'EC'
        case 'Ed':
            return 'OKP'
        default:
            throw new JoseNotSupported('Unsupported "alg" value for a JSON Web Key Set')
    }
}

interface Cache {
    [alg: string]: KeyLike;
}

function isJwksLike(jwks: unknown): jwks is JsonWebKeySet {
    return (
        Boolean(jwks) &&
        typeof jwks === 'object' &&
        Array.isArray((jwks as JsonWebKeySet).keys)
    );
}

class LocalJwkSet {
    #jwks: JsonWebKeySet;
    #cached: WeakMap<Jwk, Cache> = new WeakMap();

    constructor(jwks: unknown) {
        if (!isJwksLike(jwks)) {
            throw new JwksInvalid('JSON Web Key Set malformed')
        }
        this.#jwks = structuredClone<JsonWebKeySet>(jwks);
    }

    jwks(): JsonWebKeySet {
        return this.#jwks;
    }

    async getKey(
        protectedHeader?: JwsHeaderParameters,
        token?: FlattenedJwsInput
    ): Promise<KeyLike> {
        const {alg, kid} = {...protectedHeader, ...token?.header}
        const kty = getKtyFromAlg(alg);
        const candidates = this.#jwks.keys.filter((jwk) => {
            let candidate = jwk.kty === kty;

            if (candidate && typeof kid === 'string') {
                candidate = jwk.kid === kid;
            }

            if (candidate && typeof jwk.alg === 'string') {
                candidate = jwk.alg === alg;
            }

            if (candidate && typeof jwk.use === 'string') {
                candidate = jwk.use === 'sig';
            }

            return candidate;
        });

        const {0: jwk, length} = candidates;
        if (length === 0) {
            throw new JwksNoMatchingKey();
        }
        if (length !== 1) {
            throw new Error('Multiple matching keys found');
        }
        return importWithAlgCache(this.#cached, jwk, alg!);
    }
}

async function importWithAlgCache(
    cache: WeakMap<Jwk, Cache>,
    jwk: Jwk,
    alg: string) {
    const cached = cache.get(jwk) || cache.set(jwk, {}).get(jwk)!;
    if (cached[alg] === undefined) {
        const key = KEYUTIL.getKey(jwk as KJUR.jws.JWS.JsonWebKey);
        if (key instanceof RSAKey) {
            cached[alg] = key;
        } else {
            throw new Error(`RSA key expected!`);
        }
    }
    return cached[alg];
}

export function createLocalJwkSet(
    jwks: JsonWebKeySet
): (
    protectedHeader?: JwsHeaderParameters,
    token?: FlattenedJwsInput
) => Promise<KeyLike> {
    const set = new LocalJwkSet(jwks);

    const localJwksSet = async (
        protectedHeader?: JwsHeaderParameters,
        token?: FlattenedJwsInput
    ): Promise<KeyLike> => {
        return await set.getKey(protectedHeader, token);
    }
    Object.defineProperties(localJwksSet, {
        jwks: {
            value: () => structuredClone(set.jwks()),
            enumerable: false,
            configurable: false,
            writable: false,
        }
    });
    return localJwksSet;
}
